import type { Middleware } from '@reduxjs/toolkit';
import { jwtDecode } from 'jwt-decode';

import apiSlice from 'src/store/api/apiSlice';
import { logOut, setCredentials } from 'src/store/reducers/auth';
import { cartReset } from 'src/store/reducers/cart';

type AuthStateShape = {
    auth: {
        access: string;
    };
};

type AccessClaims = { user_id?: string | number; username?: string; email?: string };

/**
 * Who an access token belongs to, or null for a guest.
 *
 * Comparing raw token strings treated a *token refresh* as a credential
 * boundary: the reauth wrapper dispatches `setCredentials` with a freshly
 * issued access token for the same person, which reset the whole API slice and
 * aborted the in-flight request that triggered the refresh, leaving that view
 * stuck on its loading state. Only a login, logout or account switch changes
 * the identity, and only those may drop identity-dependent prices.
 */
const identityOf = (access: string): string | null => {
    if (!access) {
        return null;
    }

    try {
        const claims = jwtDecode<AccessClaims>(access);
        const id = claims.user_id ?? claims.username ?? claims.email;
        return id === undefined || id === null || id === '' ? null : String(id);
    } catch {
        // An unreadable token is its own boundary: fall back to the raw string
        // so a malformed pair still resets rather than silently reusing the
        // previous identity's cached prices.
        return access;
    }
};

/** Keep user-priced and user-owned API data scoped to one authenticated identity. */
export const authCacheMiddleware: Middleware = (storeApi) => (next) => (action) => {
    const previousAccess = (storeApi.getState() as AuthStateShape).auth.access;
    const result = next(action);
    const nextAccess = (storeApi.getState() as AuthStateShape).auth.access;

    if (
        setCredentials.match(action)
        && identityOf(previousAccess) !== identityOf(nextAccess)
    ) {
        // Invalidating an actively mounted query refetches it but leaves the
        // previous user's price visible in `data` until the request completes.
        // Resetting the shared API slice removes that response synchronously;
        // mounted hooks then rehydrate under the new Authorization header.
        storeApi.dispatch(apiSlice.util.resetApiState());
    } else if (logOut.match(action) && previousAccess && !nextAccess) {
        storeApi.dispatch(cartReset());
        storeApi.dispatch(apiSlice.util.resetApiState());
    }

    return result;
};
